import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { cx } from './classNames';

// MUI's small outlined Select in the app theme (MuiOutlinedInput, MuiSelect
// in styles/msiMuiComponentOverrides.js), as TextInput draws the field: the
// control height (36px, 30px compact), 7px radius, dark line border, 12px
// ink text, 9px left padding and room on the right for the chevron; hover
// darkens the border, focus takes the focus border and a 2px focus ring,
// disabled takes the disabled fill. The options list is the platform's own
// (dark in the dark themes).
const SELECT_CLASS_NAME = cx(
    'peer m-0 box-border h-9 w-full min-w-0 cursor-pointer appearance-none rounded-[7px] border border-solid',
    'border-ui-line-strong bg-ui-surface py-0 pl-[9px] pr-7 [font-family:inherit] text-[12px] leading-[1.4375]',
    'text-ui-ink hover:border-ui-hover-line focus:border-ui-focus focus:outline-none',
    'transition-[border-color,box-shadow] duration-100 focus:shadow-[0_0_0_2px_var(--app-focusRing)]',
    'disabled:cursor-default disabled:border-ui-disabled-line disabled:bg-ui-disabled-fill',
    'disabled:text-ui-disabled-text [[data-compact]_&]:h-[30px] dark:[color-scheme:dark]',
);
const ICON_CLASS_NAME = 'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ui-muted '
    + 'peer-disabled:text-ui-disabled-text';

/**
 * A single-choice dropdown: a native <select> (so keyboard, roles and the
 * options list are the browser's) with a chevron. `children` are its
 * <option> elements. `className` goes to the wrapper (width, margins); other
 * props (value, onChange(event), disabled, aria-label…) go to the select.
 */
const Select = forwardRef(function Select({ className, children, ...selectProps }, ref) {
    return (
        <span className={cx('relative inline-flex align-middle', className)}>
            <select ref={ref} className={SELECT_CLASS_NAME} {...selectProps}>
                {children}
            </select>
            <ChevronDown aria-hidden="true" size={16} strokeWidth={2} className={ICON_CLASS_NAME} />
        </span>
    );
});

Select.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
};

export default Select;
